import { useEffect, useState } from 'react';
import { IImportResponse, ImportState } from '~/Models/DTO/Import.model';
import { useSelector } from 'react-redux';
import { selectCurrentImport } from '~/store/imports';
import { useNavigation, useProvider } from '~/Hooks';
import { Route } from '~/Constants/Routes.constants';
import { generatePath } from 'react-router-dom';
import { useLocation } from 'react-use';
import { useRequestImportDataScheme } from '~/requests/importService/useRequestImportDataScheme';
import { useRequestImportCustomerData } from '~/requests/importService/useRequestImportCustomerData';

const { IMPORT_REVIEW, IMPORT, IMPORT_COMPLETE } = Route;

const getStatusStep = (importStatus: ImportState) => {
  switch (importStatus) {
    case 'ParsingFile':
    case 'FileParsingFailed':
    case 'New':
      return 0;
    case 'FileParsed':
    case 'ProcessingFailed':
    case 'ValidationFailed':
      return 1;
    default:
      return 2;
  }
};

const getCurrentStepFromURL = (pathname?: string, domain: string = '') => {
  switch (pathname) {
    case generatePath(IMPORT, { domain }):
      return 0;
    case generatePath(IMPORT_REVIEW, { domain }):
      return 1;
    case generatePath(IMPORT_COMPLETE, { domain }):
    default:
      return 2;
  }
};

export const useImport = () => {
  const [title, setTitle] = useState('');
  const [headers, setHeaders] = useState(<Array<string>>[]);
  const [tableData, setTableData] = useState<IImportResponse[]>([]);
  const [showProblems, setShowProblems] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const currentImport = useSelector(selectCurrentImport);

  const { currentProvider } = useProvider();
  const { dataScheme, refetchImportDataScheme, dataSchemeError } = useRequestImportDataScheme();
  const {
    customerData,
    refetchImportCustomerData,
    customerDataError,
  } = useRequestImportCustomerData({
    importId: currentImport?.id || '0',
    showProblems,
  });

  const { navigate } = useNavigation();
  const { pathname } = useLocation();

  useEffect(() => {
    if (dataScheme) {
      const headers = dataScheme[0].properties.map((column) => column.name);
      headers.push('validationErrors');
      setTitle(dataScheme[0].name);
      setHeaders(headers);
    }
  }, [dataScheme]);

  useEffect(() => {
    if (customerData) {
      setTableData(customerData.records);
    }
  }, [customerData]);

  useEffect(() => {
    if (currentImport?.status) {
      const step = getStatusStep(currentImport?.status);
      if (
        step === 0 &&
        pathname !== generatePath(IMPORT, { domain: currentProvider?.domain || '' })
      ) {
        navigate(IMPORT);
      } else if (
        step === 1 &&
        pathname !== generatePath(IMPORT_REVIEW, { domain: currentProvider?.domain || '' })
      ) {
        navigate(IMPORT_REVIEW);
      } else if (
        step === 2 &&
        pathname !== generatePath(IMPORT_COMPLETE, { domain: currentProvider?.domain || '' })
      ) {
        navigate(IMPORT_COMPLETE);
      }
    }

    refetchImportDataScheme();
    if (currentImport?.id) {
      refetchImportCustomerData();
    }
  }, [currentImport?.status, showProblems]);

  return {
    currentStep: getCurrentStepFromURL(pathname, currentProvider?.domain),
    tableData,
    title,
    headers,
    uploadStatus: currentImport?.status,
    showProblems,
    setShowProblems,
    modalOpen,
    setModalOpen,
    error: dataSchemeError || customerDataError,
  };
};
